'use client';
import React from 'react';
import { CheckCircle2, AlertTriangle } from 'lucide-react';

const CONDITIONS = [
  'Convert partnership firm into a Private Limited Company with clear shareholding.',
  'Execute clean, registered 30-year lease deed for the 10–12 acre parcel.',
  'Obtain at least one written vendor registration/LOI from NTPC, Rosa or equivalent TPP.',
  'Finalise machinery quotation from proven OEM for 5 TPH line within ₹3.0–3.5 Cr envelope.',
  'Secure CGTMSE-backed term loan + WC sanction with DSCR covenant ≥ 1.50x.',
  'Maintain minimum ₹1.4 Cr working capital buffer at COD; do not treat WC as residual.',
  'Complete environmental / consent-to-establish clearances before civil works.',
  '(Optional) Explore Phase-2 500–1000 kW solar once Year-1 cash flows are proven.',
];

export default function RecommendationCard() {
  return (
    <div className="exec-panel">
      <div className="exec-panel-header">
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(201,168,76,0.12)' }}
          >
            <CheckCircle2 size={18} style={{ color: 'var(--gold)' }} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3
                style={{
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  letterSpacing: '0.02em',
                }}
              >
                Investment Committee Recommendation
              </h3>
              <span className="badge-success">GO WITH CONDITIONS</span>
            </div>
            <p
              style={{
                fontSize: '0.68rem',
                color: 'var(--text-tertiary)',
                marginTop: 4,
                lineHeight: 1.5,
              }}
            >
              Overall Project Data Confidence Score:{' '}
              <span
                style={{ fontWeight: 700, color: 'var(--gold)', fontFamily: 'var(--font-mono)' }}
              >
                7.0 / 10
              </span>{' '}
              — rises to 8.5+/10 once offtake LOI, final quotes, incorporation and lease are
              complete.
            </p>
          </div>
        </div>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
          {CONDITIONS?.map((condition, idx) => (
            <div
              key={`condition-${idx}`}
              className="flex items-start gap-2.5 p-2.5 rounded"
              style={{ background: 'var(--surface-2)', border: '1px solid var(--divider)' }}
            >
              <span
                className="w-5 h-5 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{
                  background: 'rgba(201,168,76,0.12)',
                  color: 'var(--gold)',
                  fontSize: '0.6rem',
                  fontWeight: 800,
                }}
              >
                {idx + 1}
              </span>
              <p style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {condition}
              </p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div
            className="p-3 rounded"
            style={{
              background: 'rgba(201,168,76,0.05)',
              border: '1px solid rgba(201,168,76,0.2)',
            }}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <CheckCircle2 size={13} style={{ color: 'var(--gold)' }} />
              <p
                style={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: 'var(--gold)',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                Bank Financeable
              </p>
            </div>
            <p style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
              CGTMSE eligible — with proper DPR, offtake evidence, Udyam registration, DSCR covenant
            </p>
          </div>
          <div
            className="p-3 rounded"
            style={{
              background: 'rgba(74,158,255,0.05)',
              border: '1px solid rgba(74,158,255,0.2)',
            }}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <CheckCircle2 size={13} style={{ color: 'var(--accent)' }} />
              <p
                style={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: 'var(--accent)',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                Investor Attractive
              </p>
            </div>
            <p style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
              More attractive at 5 TPH than 2 TPH — better scale economics for strategic and
              financial investors
            </p>
          </div>
          <div
            className="p-3 rounded"
            style={{
              background: 'rgba(232,160,32,0.05)',
              border: '1px solid rgba(232,160,32,0.2)',
            }}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <AlertTriangle size={13} style={{ color: 'var(--warning)' }} />
              <p
                style={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: 'var(--warning)',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                Key Risk
              </p>
            </div>
            <p style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
              Feedstock supply + offtake volume shortfall are the two highest-impact risks. Mitigate
              with contracts before drawdown.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
